<template>
  <div class="jobs-list">
    <page-list-header>
      <template #left>
        <b-button v-if="canAddJobs" variant="outline-primary" @click="showCreateModal" class="mr-2">
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />{{ $t('jobs.addJobButton') }}
        </b-button>
        <inline-search v-if="!fixedSearchText" v-model="searchText" @search="getJobs" />
      </template>
      <template #right>
        <b-form-group label-cols="auto" :label="$t('common.perPageLabel')" class="mb-0">
          <b-form-select v-model="perPage" :options="PER_PAGE_OPTIONS" />
        </b-form-group>
        <export-button
          file-name="jobs"
          :export-data="jobs"
          :export-file-rows="exportFileRows"
          :export-headers="exportHeaders"
        />
      </template>
    </page-list-header>

    <component :is="componentType" class="mb-3">
      <app-loading :loading="loading" />

      <b-table
        v-if="!loading"
        ref="table"
        primary-key="job_id"
        :items="paginatedJobs"
        :fields="fieldDefinitions"
        class="mb-0"
        table-class="table-minimal"
        :empty-text="$t('common.noRecordsToShow')"
        show-empty
        no-footer-sorting
        no-local-sorting
        responsive
      >
        <template v-slot:cell(actions)="data">
          <div class="d-flex justify-content-center">
            <b-button variant="outline-secondary" size="sm" @click="showEditModal(data.item)" class="mr-1">
              <font-awesome-icon :icon="['fal', 'pen-to-square']" />
            </b-button>
            <b-button variant="outline-danger" size="sm" @click="confirmDelete(data.item)">
              <font-awesome-icon :icon="['fal', 'times']" />
            </b-button>
          </div>
        </template>
      </b-table>
    </component>

    <b-pagination
      v-model="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      @change="changePage"
      class="justify-content-end"
    />

    <!-- Create/Edit Job Modal -->
    <b-modal
      v-model="showJobModal"
      :title="isEditing ? $t('jobs.editJobTitle') : $t('jobs.createJobTitle')"
      :ok-title="$t('jobs.saveButton')"
      :cancel-title="$t('common.cancelButton')"
      @hidden="resetForm"
      @ok="saveJob"
      :ok-disabled="!isFormValid"
    >
      <b-form>
        <b-form-group :label="$t('jobs.fields.jobCode')" label-for="job-code">
          <b-form-input
            id="job-code"
            v-model="form.job_code"
            :state="jobCodeValidation"
            :placeholder="$t('jobs.enterJobCodePlaceholder')"
          />
          <b-form-invalid-feedback>{{ $t('jobs.jobCodeRequiredError') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="$t('jobs.fields.hourlyRate')" label-for="hourly-rate">
          <b-form-input
            id="hourly-rate"
            v-model.number="form.hourly_rate"
            type="number"
            step="0.01"
            :placeholder="$t('jobs.enterHourlyRatePlaceholder')"
          />
        </b-form-group>

        <b-form-group :label="$t('jobs.fields.multiplier')" label-for="multiplier">
          <b-form-input
            id="multiplier"
            v-model.number="form.multiplier"
            type="number"
            step="0.01"
            :placeholder="$t('jobs.enterMultiplierPlaceholder')"
          />
        </b-form-group>

        <small class="text-muted">{{ $t('jobs.rateOrMultiplierNote') }}</small>
      </b-form>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('jobs.confirmDeleteTitle')"
      @ok="deleteJob"
      ok-variant="danger"
      :ok-title="$t('common.deleteButton')"
      :cancel-title="$t('common.cancelButton')"
    >
      <p>
        <i18n path="jobs.deleteConfirmBody" tag="span">
          <template #jobCode
            ><strong>{{ selectedJob?.job_code }}</strong></template
          >
        </i18n>
      </p>
      <p class="text-danger">{{ $t('jobs.deleteConfirmWarning') }}</p>
    </b-modal>
  </div>
</template>

<script>
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import InlineSearch from '@/components/InlineSearch.vue';
import PageListHeader from '@/components/PageListHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { getJobFieldDefinitions, PER_PAGE_OPTIONS } from '@/pages/jobs/constants';

export default {
  name: 'JobsList',
  components: {
    AppLoading,
    ExportButton,
    InlineSearch,
    PageListHeader,
    SmeCard,
  },
  props: {
    nested: {
      type: Boolean,
      default: false,
    },
    fixedSearchText: {
      type: String,
    },
    employeeId: {
      type: String,
    },
  },
  setup() {
    const { canAddJobs, canViewJobs } = useFeatureFlags();
    return {
      canAddJobs,
      canViewJobs,
    };
  },
  data() {
    return {
      PER_PAGE_OPTIONS,
      jobs: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      searchText: this.fixedSearchText || '',
      loading: true,
      isEditing: false,
      selectedJob: null,
      showJobModal: false,
      showDeleteModal: false,
      form: {
        job_code: '',
        hourly_rate: null,
        multiplier: null,
      },
      state: State.state,
    };
  },
  computed: {
    fieldDefinitions() {
      if (this.canAddJobs) {
        return getJobFieldDefinitions();
      } else {
        return getJobFieldDefinitions().filter(field => field.key !== 'actions');
      }
    },
    componentType() {
      return this.nested ? 'div' : SmeCard;
    },
    exportHeaders() {
      return this.fieldDefinitions.map(field => field.label);
    },
    jobCodeValidation() {
      return this.form.job_code.trim() ? true : false;
    },
    isFormValid() {
      return this.form.job_code.trim();
    },
    filteredJobs() {
      let filtered = this.jobs;

      // Apply search filter
      if (this.searchText.trim()) {
        const searchTerm = this.searchText.toLowerCase().trim();
        filtered = filtered.filter(job => job.job_code?.toLowerCase().includes(searchTerm));
      }

      // Apply employee filter if specified
      if (this.employeeId) {
        filtered = filtered.filter(job => job.employee_id === this.employeeId);
      }

      return filtered;
    },
    paginatedJobs() {
      const startIndex = (this.currentPage - 1) * this.perPage;
      const endIndex = startIndex + this.perPage;
      return this.filteredJobs.slice(startIndex, endIndex);
    },
  },
  watch: {
    perPage() {
      this.currentPage = 1;
    },
    searchText() {
      this.currentPage = 1;
    },
    filteredJobs: {
      immediate: true,
      handler(newFilteredJobs) {
        this.totalRows = newFilteredJobs.length;
      },
    },
  },
  async mounted() {
    await this.getJobs();
  },
  methods: {
    async getJobs() {
      if (!this.canViewJobs) {
        this.jobs = [];
        this.loading = false;
        return;
      }

      this.loading = true;

      try {
        const allJobs = await ApiClient.getJobsForCompany();
        this.jobs = allJobs || [];
      } catch (error) {
        console.error('Error fetching jobs:', error);
        this.jobs = [];
      } finally {
        this.loading = false;
      }
    },

    changePage(newPageNumber) {
      this.currentPage = newPageNumber;
    },

    exportFileRows(item) {
      return this.fieldDefinitions.map(field =>
        field.formatter ? field.formatter(item[field.key], field.key, item) : item[field.key],
      );
    },

    showCreateModal() {
      this.isEditing = false;
      this.selectedJob = null;
      this.resetForm();
      this.showJobModal = true;
    },

    showEditModal(job) {
      this.isEditing = true;
      this.selectedJob = job;
      this.form = {
        job_code: job.job_code || '',
        hourly_rate: job.hourly_rate !== null && job.hourly_rate !== undefined ? job.hourly_rate / 100 : null,
        multiplier: job.multiplier || null,
      };
      this.showJobModal = true;
    },

    resetForm() {
      this.form = {
        job_code: '',
        hourly_rate: null,
        multiplier: null,
      };
    },

    confirmDelete(job) {
      this.selectedJob = job;
      this.showDeleteModal = true;
    },

    async saveJob() {
      if (!this.canAddJobs) {
        console.error('User does not have permission to add jobs');
        return;
      }

      try {
        this.loading = true;

        const jobData = {
          job_code: this.form.job_code.trim(),
          hourly_rate: this.form.hourly_rate ? Math.round(this.form.hourly_rate * 100) : null,
          multiplier: this.form.multiplier || null,
        };

        if (!this.isEditing) {
          jobData.company_id = this.state.company.company_id;
        }

        if (this.isEditing) {
          await ApiClient.updateJob(this.selectedJob.job_id, jobData);
        } else {
          await ApiClient.createJob(jobData);
        }

        await this.getJobs();
        this.resetForm();
        this.showJobModal = false;
      } catch (error) {
        console.error('Error saving job:', error);
      } finally {
        this.loading = false;
      }
    },

    async deleteJob() {
      if (!this.canAddJobs) {
        console.error('User does not have permission to delete jobs');
        return;
      }

      try {
        this.loading = true;
        await ApiClient.deleteJob(this.selectedJob.job_id);
        await this.getJobs();
        this.showDeleteModal = false;
      } catch (error) {
        console.error('Error deleting job:', error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.jobs-list {
  .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>
